import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Globe2 } from "lucide-react";
import { apiClient, formatApiError } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { toast } from "sonner";

/** PayPal redirect flow — return_url: ?token=<paypal_order_id>.
 * Captures the payment, marks the order paid, redirects to tracking. */
export default function PaypalReturn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [params] = useSearchParams();
  const [error, setError] = useState(null);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = params.get("token");
    if (!token) {
      navigate("/cart", { replace: true });
      return;
    }
    apiClient
      .post("/payments/paypal/capture-token", { token })
      .then(({ data }) => {
        if (data.paid) {
          clear();
          toast.success(t("pp.confirmedToast", { ref: data.reference ? ` (réf. ${data.reference})` : "" }));
          navigate(`/orders/${data.order_id}`, { replace: true });
        } else {
          setError(t("pp.notConfirmed"));
        }
      })
      .catch((err) => setError(formatApiError(err)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="paypal-return-page">
      <Globe2 className="w-10 h-10 mx-auto text-[#003087] mb-4" />
      {error ? (
        <>
          <h1 className="font-display text-2xl font-semibold mb-2">{t("pp.failTitle")}</h1>
          <p className="text-sm text-mute mb-6" data-testid="paypal-return-error">{error}</p>
          <button onClick={() => navigate("/checkout")} data-testid="paypal-return-retry" className="rounded-full bg-brand-red text-white px-6 py-3 font-semibold">
            Revenir au paiement
          </button>
        </>
      ) : (
        <>
          <Loader2 className="w-6 h-6 mx-auto animate-spin text-mute mb-3" />
          <h1 className="font-display text-2xl font-semibold">{t("pp.confirming")}</h1>
          <p className="text-sm text-mute mt-2">{t("pp.dontClose")}</p>
        </>
      )}
    </div>
  );
}
